import { FC, useCallback, useEffect, useState } from 'react';
import api from '@/utils/api';
import { getErrorMessage } from '@/utils/error';
import { EditableDataTable, EditableColumn, Dropdown, DropdownItem, ConfirmationModal, Button } from '@/components/ui';
import { EditUserForm } from '@/features/users';
import type { ExternalUser, UsersListResponse, UpdateUserPayload } from '@/features/users';
import { Edit, Trash2 } from 'lucide-react';
import { useToast } from '@/contexts/ToastContext';
import { useDebounce } from '@/hooks';
import { DEBOUNCE_DELAY } from '@/constants';

const Users: FC = () => {
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);
  const [limit] = useState(10);
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<UsersListResponse | null>(null);
  const [editing, setEditing] = useState<ExternalUser | null>(null);
  const [deleting, setDeleting] = useState<ExternalUser | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const { addToast } = useToast();

  const fetchData = useCallback(async (searchQuery: string) => {
    setLoading(true);
    try {
      const res = await api.get<UsersListResponse>('/users/external', {
        params: { q: searchQuery || undefined, page, limit }
      });
      setData(res.data);
    } finally {
      setLoading(false);
    }
  }, [page, limit]);

  // Debounced search function
  const debouncedFetch = useDebounce((searchQuery: string) => {
    setPage(1); // Reset to first page on new search
    fetchData(searchQuery);
  }, DEBOUNCE_DELAY);

  // Initial load and page changes
  useEffect(() => {
    fetchData(search);
  }, [page]); // eslint-disable-line react-hooks/exhaustive-deps

  // Debounced search on search term change
  useEffect(() => {
    debouncedFetch(search);
  }, [search]); // eslint-disable-line react-hooks/exhaustive-deps

  const openEdit = (user: ExternalUser) => {
    setEditing(user);
  };

  const handleEditSubmit = async (payload: UpdateUserPayload) => {
    if (!editing) return;
    await api.patch(`/users/${editing.id}`, payload);
    setEditing(null);
    fetchData(search);
  };

  const handleEditCancel = () => {
    setEditing(null);
  };

  const handleDelete = (user: ExternalUser) => {
    setDeleting(user);
  };

  const confirmDelete = async () => {
    if (!deleting) return;
    setIsDeleting(true);
    try {
      await api.delete(`/users/${deleting.id}`);
      addToast(`User "${deleting.name}" has been deleted successfully`, 'success');
      setDeleting(null);
      fetchData(search);
    } catch (error: unknown) {
      addToast(getErrorMessage(error), 'error');
    } finally {
      setIsDeleting(false);
    }
  };

  const cancelDelete = () => {
    setDeleting(null);
  };

  // EditableDataTable callbacks
  const handleDataUpdate = async (_updatedData: ExternalUser[]) => {
    try {
      // For now, just update local state. You could implement batch updates here
    } catch (error) {
      console.error('Failed to update data:', error);
      addToast('Failed to update data', 'error');
    }
  };

  const handleRowUpdate = async (_oldRow: ExternalUser, newRow: ExternalUser) => {
    try {
      await api.patch(`/users/${newRow.id}`, newRow);
      addToast(`User "${newRow.name}" has been updated successfully`, 'success');
      fetchData(search);
    } catch (error: unknown) {
      addToast(getErrorMessage(error), 'error');
      throw error; // Let the table revert the change
    }
  };

  const columns: EditableColumn<ExternalUser>[] = [
    {
      key: 'name',
      header: 'Name',
      editable: true,
      validate: (value: string) => {
        if (!value?.trim()) return 'Name is required';
        if (value.length < 2) return 'Name must be at least 2 characters';
        return undefined;
      },
    },
    {
      key: 'email',
      header: 'Email',
      editable: true,
      validate: (value: string) => {
        if (!value?.trim()) return 'Email is required';
        const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
        if (!emailRegex.test(value)) return 'Invalid email format';
        return undefined;
      },
    },
    {
      key: 'company',
      header: 'Company',
      editable: true,
    },
    {
      key: 'phone',
      header: 'Phone',
      editable: true,
    },
    {
      key: 'lastLogin',
      header: 'Last Login',
      editable: false,
    },
    {
      key: 'actions',
      header: 'Actions',
      editable: false,
      enableEditing: false,
      render: (row) => {
        const items: DropdownItem[] = [
          {
            key: 'edit',
            label: 'Edit',
            icon: Edit,
            onClick: () => openEdit(row),
            variant: 'info',
          },
          {
            key: 'delete',
            label: 'Delete',
            icon: Trash2,
            onClick: () => handleDelete(row),
            variant: 'danger',
          },
        ];

        return <Dropdown items={items} />;
      }
    },
  ];

  // const totalPages = Math.ceil((data?.total ?? 0) / limit);
  // const startItem = (page - 1) * limit + 1;
  // const endItem = Math.min(page * limit, data?.total ?? 0);

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">Users</h1>
          <p className="text-sm text-gray-500 mt-1">Manage external user accounts</p>
        </div>
        <div className="flex gap-2">
          <input
            className="border border-gray-300 rounded px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-gray-900 focus:border-transparent"
            placeholder="Search name or email"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                setPage(1);
                fetchData(search);
              }
            }}
          />
          <Button
            variant="primary"
            size="sm"
            onClick={() => { setPage(1); fetchData(search); }}
          >
            Search
          </Button>
        </div>
      </div>

      {/* Data Table */}
      <EditableDataTable
        title="External Users"
        columns={columns}
        data={loading ? [] : (data?.items ?? [])}
        enableEditing={true}
        enableDelete={true}
        onDataUpdate={handleDataUpdate}
        onRowUpdate={handleRowUpdate}
        initialEditingMode="cell"
        className="border border-gray-200 rounded-lg overflow-hidden"
      />

      {editing && (
        <EditUserForm
          user={editing}
          onSubmit={handleEditSubmit}
          onCancel={handleEditCancel}
        />
      )}

      <ConfirmationModal
        isOpen={!!deleting}
        title="Delete User"
        message={`Are you sure you want to delete "${deleting?.name}"? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        variant="danger"
        onConfirm={confirmDelete}
        onCancel={cancelDelete}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Users;


